import { useSyncExternalStore } from 'react';

/**
 * Whether the app's single SSE connection is currently up.
 *
 * The connection itself lives in sidebar.tsx — one EventSource per tab, for as
 * long as the user is signed in — and until now its state was local to that
 * component. Screens that are fed by pushes need to know about it too: a
 * stream that is down turns "wait for the next event" into "wait for ever".
 *
 * So this is the fallback switch, and the only thing it is for. A screen reads
 * it to decide whether it needs a backstop poll at all:
 *
 *     const isStreamConnected = useEventStreamConnected();
 *     useSomeQuery(arg, { pollingInterval: isStreamConnected ? 0 : 30000 });
 *
 * While the stream is up that is literally zero polling — the pushes do the
 * work. It only starts making requests when there is nothing else to listen
 * to, and stops again the moment the stream comes back.
 *
 * Kept as a module-level store rather than Redux state because the writer is
 * an EventSource callback that fires outside React's ownership, and every
 * reader wants a plain boolean. `useSyncExternalStore` gives that a tearing-free
 * subscription without a dispatch on every reconnect blip.
 */

let connected = false;
const listeners = new Set<() => void>();

/** Called by the sidebar's EventSource handlers. Not for general use. */
export function setEventStreamConnected(next: boolean): void {
  if (next === connected) return;
  connected = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const getSnapshot = () => connected;

// There is no EventSource during SSR, so the server must render the same
// markup the client renders before the stream opens: not connected.
const getServerSnapshot = () => false;

export function useEventStreamConnected(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
